import { useCallback, useEffect, useRef, useState } from "react";

import { BulletinFormValues } from "@/components/alerts/add-v2/model/types";
import { useBulletinValues } from "@/components/alerts/add-v2/state/bulletinFormContext";

import { BulletinRepository } from "./bulletinRepository";

/**
 * Autosave — README decision 16, with one trigger fewer than that decision names.
 *
 * It said "field blur + array-item save + 800 ms idle". There is no longer an array-item save:
 * entities write straight into the document like every other row list here, so an entity edit is
 * an ordinary change and the idle timer already covers it.
 *
 * The payload is the **whole document**. Per-field payloads would need merge semantics server-side
 * for no gain on something this small, and the PATCH endpoint is already a full-document overwrite.
 *
 * ## Failing
 *
 * A save that fails is **retried once, immediately**, and only then reported. That covers the
 * failure this actually sees — one dropped request in a cruiser with patchy coverage — without
 * making the officer do anything. When the second attempt fails too the content stays dirty, the
 * status goes to `error`, and `retry` is what the indicator's button calls. Nothing is ever
 * silently dropped: the next change would resend it anyway, because the baseline was rolled back.
 *
 * ## Stopping
 *
 * `enabled` exists for one moment: **finalizing is one-way.** The endpoint refuses to put a
 * published bulletin back into draft, so a save that lands after `finalize` resolves sends
 * `is_draft: true` and is rejected. The page turns this off before publishing and back on if
 * publishing fails — leaving it off after a failure would be worse than the bug, since the officer
 * would go on typing into a form that no longer saves.
 */

const IDLE_MS = 800;

export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

export interface Autosave {
  status: AutosaveStatus;
  /** When the last successful save landed, for the indicator. `null` until one has. */
  savedAt: Date | null;
  /**
   * Spread onto the element wrapping the form. React's blur bubbles, so one handler covers every
   * field on the page — the same trick `FormSection` uses to report its own focus.
   */
  onBlurCapture: () => void;
  /** Sends the outstanding change again, after both attempts failed. */
  retry: () => void;
}

/**
 * Cleans the form values up into what should actually be sent — dropping row scaffolding the form
 * needs but the bulletin does not. Both the change detection and the payload use its result, so a
 * change that this filters away does not fire a save at all.
 */
export type PreparePayload = (values: BulletinFormValues) => BulletinFormValues;

export const useAutosave = (
  repository: BulletinRepository,
  prepare: PreparePayload = (values) => values,
  enabled = true,
): Autosave => {
  // The whole document, so this hook re-renders on every keystroke. Call it from the page shell,
  // which already subscribes to everything — never from a section.
  const values = useBulletinValues();
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout>>();
  /**
   * Serialized form of what was last sent, or `null` when the outstanding content is dirty — which
   * is what a failed save leaves behind, so the next change resends it.
   */
  const lastSaved = useRef<string | null>(null);
  /**
   * Separate from `lastSaved` on purpose. Using `null` for both "not seeded yet" and "dirty" was a
   * real bug: after a failed save the effect took the seeding branch, adopted the failed content as
   * the baseline, and the change that should have resent it was swallowed instead.
   */
  const seeded = useRef(false);
  /** Read inside `flush`, so a save fired from a stale closure still sends current values. */
  const latest = useRef(values);
  latest.current = values;
  /** Read inside `flush` for the same reason: a timer scheduled before the switch flipped. */
  const isEnabled = useRef(enabled);
  isEnabled.current = enabled;
  /**
   * The tail of the save queue, the same arrangement `media/useMediaUploads.ts` uses and for the
   * same reason. `repository.save` is a whole-document `PATCH`, so two in flight at once is a
   * lost-update race: the idle timer sends snapshot A, the officer types and blurs before A
   * resolves, `onBlurCapture` sends snapshot B, and if A lands last the server keeps the older
   * document. `lastSaved` cannot prevent it — it only suppresses an *identical* payload.
   *
   * Each task swallows its own failures, so a failed save never rejects the chain and cannot
   * strand the ones queued behind it.
   */
  const chain = useRef<Promise<void>>(Promise.resolve());

  const send = useCallback(async () => {
    if (!isEnabled.current) return;

    // Read here rather than at enqueue time: while this task waited its turn the document moved
    // on, and there is no point sending what is already stale.
    const payload = prepare(latest.current);
    // `Dayjs` serializes through its own `toJSON`, so this compares by value.
    const snapshot = JSON.stringify(payload);
    if (snapshot === lastSaved.current) return;
    lastSaved.current = snapshot;

    setStatus("saving");

    const attempt = async () => {
      try {
        const saved = await repository.save(payload);
        return saved.savedAt;
      } catch {
        return null;
      }
    };

    // One retry, then report. See "Failing" above.
    const at = (await attempt()) ?? (await attempt());
    if (at) {
      setSavedAt(at);
      setStatus("saved");
      return;
    }

    // The dirty baseline is rolled back so `retry` — or the next change — sends this content
    // again. Safe to do unconditionally now: nothing else adopted a baseline while this ran.
    lastSaved.current = null;
    setStatus("error");
  }, [repository, prepare]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (!isEnabled.current) return;

    // The `catch` is what makes "swallows its own failures" structural: `repository.save` is
    // already guarded, but `prepare` is a caller's function and a throw from it would leave the
    // chain rejected and every later save skipped.
    chain.current = chain.current.then(send).catch(() => undefined);
    return chain.current;
  }, [send]);

  useEffect(() => {
    // First render seeds the baseline instead of saving. Opening the form and touching nothing
    // must not create a draft row — that is problem 2 in the README's list.
    if (!seeded.current) {
      seeded.current = true;
      lastSaved.current = JSON.stringify(prepare(values));
      return;
    }

    clearTimeout(timer.current);
    if (!enabled) return;

    timer.current = setTimeout(flush, IDLE_MS);
    return () => clearTimeout(timer.current);
  }, [values, flush, prepare, enabled]);

  return { status, savedAt, onBlurCapture: flush, retry: flush };
};
